import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { Accept } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';

import { cn } from '@/lib/utils';
import type { GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Icon } from '../ui/icon';

type Props = {
  accept?: Accept;
  multiple?: boolean;
  maxSize?: number;
  onFileChange?: (files: File[]) => void;
  hasPreview?: boolean;
  size?: 'small' | 'large';
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
} & GeneralFormProps;

function RHFShadcnUploadInput({
  name,
  title,
  description,
  helperText,
  required,
  accept,
  multiple = false,
  maxSize,
  onFileChange,
  disabled,
  hasPreview = true,
  size = 'small',
}: Props) {
  const { control, getValues, setValue } = useFormContext();
  const translate = useTranslations();
  const resolvedHelperText = helperText ?? description;

  const handleDrop = (acceptedFiles: File[]) => {
    const currentFiles = (getValues(name) || []) as File[];
    const uniqueNewFiles = acceptedFiles.filter(
      newFile =>
        !currentFiles.some(
          (existingFile: File) => existingFile.name === newFile.name,
        ),
    );

    const newFiles = multiple
      ? [...currentFiles, ...uniqueNewFiles]
      : uniqueNewFiles;

    if (onFileChange) {
      onFileChange(newFiles);
    } else {
      setValue(name, newFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections }
    = useDropzone({
      onDrop: handleDrop,
      accept: accept || undefined,
      multiple,
      maxSize,
      disabled,
    });

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const files = (field.value || []) as File[];
        const hasFiles = files.length > 0;
        const sizeClasses = size === 'large' ? 'p-8' : 'p-6';

        let dropzoneText: string;
        if (isDragActive === true) {
          dropzoneText = translate('uploads.uploader.dropZoneHintHovered');
        } else if (hasFiles === true) {
          dropzoneText = `${files.length} file(s) selected`;
        } else {
          dropzoneText = translate('uploads.uploader.dropZoneHintDefault');
        }

        return (
          <FormItem className="w-full">
            {title !== undefined && title !== '' && (
              <FormLabel className="mb-1.5 block type-label-l leading-none">
                {title}
                {required === true && <span className="ml-0.5 text-error">*</span>}
              </FormLabel>
            )}
            <FormControl>
              <div>
                <div
                  {...getRootProps({
                    className: cn(
                      'flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 text-center transition-colors duration-[var(--duration-short-4)] ease-[var(--ease-emphasized)] motion-reduce:transition-none',
                      sizeClasses,
                      disabled === true
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer hover:bg-muted/60',
                      isDragActive === true && 'border-primary bg-primary/5',
                      fieldState.error !== undefined && 'border-error',
                    ),
                    style: { pointerEvents: disabled === true ? 'none' : 'auto' },
                  })}
                >
                  <input
                    {...getInputProps()}
                    disabled={disabled}
                    required={required}
                    className="hidden"
                  />
                  <Icon
                    icon="lucide:cloud-upload"
                    className="mb-2 text-muted-foreground"
                    size="lg"
                  />
                  <p className="type-body-m text-muted-foreground">
                    {dropzoneText}
                  </p>
                </div>
                {fileRejections.length > 0 && (
                  <FormMessage>
                    {translate('uploads.uploader.rejectedFiles')}
                  </FormMessage>
                )}
                {hasPreview === true && hasFiles === true && (
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {files.map((file: File, index: number) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={index} className="relative aspect-square overflow-hidden rounded-md border border-border">
                        <Image
                          src={URL.createObjectURL(file)}
                          alt=""
                          fill
                          className="object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormControl>
            {fieldState.error === undefined && resolvedHelperText !== undefined && resolvedHelperText !== '' && (
              <FormDescription className="mt-1.5 type-body-s text-on-surface-variant">
                {resolvedHelperText}
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnUploadInput;
